import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface DrawerProps {
  // false = parked off-screen (still mounted); true = slid in + a11y-trapped.
  open: boolean;
  // Called on Escape, scrim click, or the caller's own close control. The caller
  // decides what this does (e.g. an unsaved-changes guard) — the Drawer just signals intent.
  onClose: () => void;
  // aria-label fallback when no labelledById is given.
  title?: string;
  // id of the heading element inside `children` → aria-labelledby (preferred over title).
  labelledById?: string;
  // Panel width in px (defaults to the .drawer CSS value, 560).
  width?: number;
  children: React.ReactNode;
}

/**
 * Accessible slide-from-right drawer.
 *
 * Portals to <body> on purpose (task 0064): the `.page-enter` wrapper keeps a
 * filling transform, which makes it a containing block for fixed descendants —
 * that would trap this off-canvas (translateX(100%)) panel inside the scrollable
 * content area and leak a blank horizontal gutter. At <body> the panel is
 * viewport-relative again. The html/body `overflow-x:hidden` clips it when parked.
 *
 * Always mounted: scrim + panel render even when closed; we only toggle the
 * `.show` class so the CSS slide transition runs (mounting on open would kill it).
 *
 * A11y: role=dialog + aria-modal, Escape to close, focus trap (the app #root is
 * marked `inert` + aria-hidden while open — this panel is a body-level sibling of
 * #root, so it stays reachable), focus restore to the trigger on close, and body
 * scroll-lock. Motion lives in index.css (prefers-reduced-motion safe).
 */
export function Drawer({ open, onClose, title, labelledById, width, children }: DrawerProps) {
  const panelRef = useRef<HTMLElement>(null);
  // What had focus before we opened — restored on close.
  const restoreRef = useRef<HTMLElement | null>(null);

  // Escape closes; bind only while open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Focus trap (inert on app root) + initial focus + focus restore + scroll-lock.
  useEffect(() => {
    if (!open) return;
    const root = document.getElementById("root");
    restoreRef.current = document.activeElement as HTMLElement | null;
    if (root) {
      root.setAttribute("inert", "");
      root.setAttribute("aria-hidden", "true");
    }
    // Preserve whatever inline overflow was set (index.css keeps overflow-x:hidden via stylesheet).
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();

    return () => {
      if (root) {
        root.removeAttribute("inert");
        root.removeAttribute("aria-hidden");
      }
      document.body.style.overflow = prevOverflow;
      restoreRef.current?.focus?.();
    };
  }, [open]);

  return createPortal(
    <>
      <div className={`scrim ${open ? "show" : ""}`} onClick={onClose} aria-hidden />
      <aside
        ref={panelRef}
        className={`drawer ${open ? "show" : ""}`}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={width ? { width } : undefined}
        {...(labelledById ? { "aria-labelledby": labelledById } : { "aria-label": title })}
      >
        {children}
      </aside>
    </>,
    document.body,
  );
}
